import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import {
  ShoppingCart, User, Menu, Search, LayoutDashboard, Phone, Mail, MapPin,
  ChevronDown, Droplets, Zap, Lightbulb, Shield, Wrench, Pipette,
  Paintbrush, Hammer, Package, ArrowRight, X, Home, Settings, Tag, ChevronRight,
  Building2, CircleDot, Cog, Power, Drill, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const iconMap = {
  Droplets: Droplets, Zap: Zap, Lightbulb: Lightbulb, Shield: Shield,
  Wrench: Wrench, Pipette: Pipette, Paintbrush: Paintbrush, Hammer: Hammer,
  Home: Home, Settings: Settings, Tag: Tag, Building2: Building2,
  CircleDot: CircleDot, Cog: Cog, Package: Package, Power: Power, Drill: Drill
};

// subcategoriesData removed - now using database children from category.children

// Category icon mapping
const categoryIcons = {
  'Sanitaryware': Droplets,
  'Electrical': Zap,
  'Lightings': Lightbulb,
  'Safety': Shield,
  'Tools': Wrench,
  'Hardware': Hammer,
  'Cabins': Building2,
  'Bollards': CircleDot,
  'Automation': Cog,
  'Plumbing': Pipette,
  'Paints': Paintbrush,
  'Power Tools': Wrench,
};

const CategoryDropdown = ({ category, onClose, dropdownRef }) => {
  const subcategories = category.children || [];
  const IconComponent = categoryIcons[category.name] || Package;

  // Build columns (max 3)
  const colCount = subcategories.length > 6 ? 3 : subcategories.length > 3 ? 2 : 1;
  const itemsPerColumn = Math.ceil(subcategories.length / colCount);
  const columns = [];
  for (let i = 0; i < subcategories.length; i += itemsPerColumn) {
    columns.push(subcategories.slice(i, i + itemsPerColumn));
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute left-0 top-full bg-white shadow-xl rounded-b-lg z-50"
      style={{
        animation: 'megaMenuFadeIn 0.15s ease-out',
        minWidth: colCount === 1 ? 200 : colCount === 2 ? 360 : 500,
      }}
    >
      {/* Category Header */}
      <div className="flex items-center gap-4 p-4 border-b border-gray-100 bg-[#0c1b44]">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
          <IconComponent className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-white">{category.name}</h3>
          <p className="text-xs text-white/60">{subcategories.length} subcategories</p>
        </div>
      </div>

      {/* Subcategories Grid */}
      <div className="p-4">
        <div className={`grid gap-x-6 gap-y-1`} style={{ gridTemplateColumns: `repeat(${colCount}, 1fr)` }}>
          {columns.map((column, colIndex) => (
            <div key={colIndex} className="space-y-1">
              {column.map((sub) => (
                <Link
                  key={sub.id}
                  to={`/products?category=${sub.id}`}
                  onClick={onClose}
                  className="flex items-center justify-between py-1.5 px-2 rounded text-gray-600 hover:text-white hover:bg-[#0c1b44] transition-colors text-sm group/sub"
                >
                  <span>{sub.name}</span>
                  {sub.product_count > 0 && (
                    <span className="text-[10px] text-gray-400 group-hover/sub:text-white/60 ml-2">{sub.product_count}</span>
                  )}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* View All Link */}
      <div className="px-4 pb-4 pt-2 border-t border-gray-100">
        <Link
          to={`/products?category=${category.id}`}
          onClick={onClose}
          className="flex items-center gap-1 text-[#0c1b44] hover:text-[#1a2d64] font-semibold text-sm transition-colors"
        >
          View All {category.name}
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

// All Categories Mega Menu Component
const MegaMenu = ({ categories, onClose }) => {
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || null);

  const activeCat = categories.find(c => c.id === activeCategory);
  const IconComponent = activeCat ? (categoryIcons[activeCat.name] || Package) : Package;
  const subcategories = activeCat ? (activeCat.children || []) : [];

  return (
    <div
      className="absolute right-0 left-0 top-full w-full bg-white shadow-2xl z-50 border-b border-gray-200"
      style={{ animation: 'megaMenuFadeIn 0.2s ease-out' }}
      data-testid="mega-menu"
    >
      <div className="flex pl-12">
        {/* Categories List - Left Side */}
        <div className="w-56 bg-white border-r border-gray-200 py-4 flex-shrink-0">
          {categories.map((cat) => {
            const CatIcon = categoryIcons[cat.name] || Package;
            return (
              <div
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-3 px-4 py-3 transition-all cursor-pointer ${activeCategory === cat.id
                  ? 'bg-gray-50 text-gray-700'
                  : 'text-navy hover:bg-gray-50'
                  }`}
              >
                <CatIcon className={`h-4 w-4 flex-shrink-0 ${activeCategory === cat.id ? 'text-[#0c1b44]' : 'text-[#0c1b44]'}`} />
                <span className="text-sm font-medium">{cat.name}</span>
                <ChevronRight className={`h-3 w-3 ml-auto ${activeCategory === cat.id ? 'text-[#0c1b44]' : 'text-gray-400'}`} />
              </div>
            );
          })}
        </div>

        {/* Category Details - Right Side */}
        {activeCat && (
          <div className="flex-1 p-6">
            {/* Category Header */}
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100 mb-4">
              <div className="w-14 h-14 rounded-lg bg-navy/10 flex items-center justify-center flex-shrink-0">
                <IconComponent className="h-7 w-7 text-navy" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-navy">{activeCat.name}</h3>
                <p className="text-gray-500">{subcategories.length} subcategories</p>
              </div>
            </div>

            {/* Subcategories Grid - 4 columns */}
            <div className="grid grid-cols-4 gap-x-6 gap-y-3 mb-6">
              {subcategories.map((sub) => (
                <Link
                  key={sub.id}
                  to={`/products?category=${sub.id}`}
                  onClick={onClose}
                  className="text-gray-600 hover:text-[#0c1b44] transition-colors text-sm py-1"
                >
                  {sub.name}
                </Link>
              ))}
            </div>

            {/* View All Link */}
            <Link
              to={`/products?category=${activeCat.id}`}
              onClick={onClose}
              className="inline-flex items-center gap-1 text-[#0c1b44] hover:text-[#1a2d64] font-semibold transition-colors"
            >
              View All {activeCat.name}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Navigation Item with Dropdown — hover-open with debounce
const NavItemWithDropdown = ({ category, isActive, onClick, onClose, showDropdown, onMouseEnter, onMouseLeave }) => {
  const hasChildren = category.children && category.children.length > 0;
  const navigate = useNavigate();
  const dropdownRef = React.useRef(null);

  return (
    <div
      className="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        onClick={() => {
          if (hasChildren) {
            onClick();
          } else {
            navigate(`/products?category=${category.id}`);
            if (onClose) onClose();
          }
        }}
        className={`flex items-center gap-1 transition-colors px-3 py-4 text-sm font-semibold ${
          isActive
            ? 'text-[#0c1b44] border-b-2 border-[#0c1b44]'
            : 'text-gray-700 hover:text-[#0c1b44]'
        }`}
      >
        {category.name}
        {hasChildren && (
          <ChevronDown className={`h-3 w-3 transition-transform ${isActive ? 'rotate-180 text-[#0c1b44]' : 'text-gray-400'}`} />
        )}
      </button>

      {hasChildren && showDropdown && (
        <CategoryDropdown category={category} onClose={onClose} dropdownRef={dropdownRef} />
      )}
    </div>
  );
};

const Header = () => {
  const { user, cart, logout, categories, settings } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const navLeaveTimer = useRef(null);

  // Debounced close so cursor can travel from nav item to dropdown without it closing
  const openDropdown = (id) => {
    if (navLeaveTimer.current) clearTimeout(navLeaveTimer.current);
    setActiveDropdown(id);
    setMegaMenuOpen(false);
  };

  const openMegaMenu = () => {
    if (navLeaveTimer.current) clearTimeout(navLeaveTimer.current);
    setMegaMenuOpen(true);
    setActiveDropdown(null);
  };

  const scheduleClose = () => {
    navLeaveTimer.current = setTimeout(() => {
      setActiveDropdown(null);
      setMegaMenuOpen(false);
    }, 150);
  };

  const cancelClose = () => {
    if (navLeaveTimer.current) clearTimeout(navLeaveTimer.current);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMegaMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (location.pathname.startsWith('/admin')) return null;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <header className="sticky top-0 z-50">
      {/* Top Bar - Hidden on mobile, visible on md+ */}
      <div className="hidden md:block bg-gray-50 text-gray-700 py-1.5 sm:py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs sm:text-sm">
          <div className="flex items-center gap-3 sm:gap-6">
            <a href={`tel:${settings.company_mobile || '+971503992163'}`} className="flex items-center gap-1 sm:gap-1.5 hover:text-[#0c1b44] transition-colors">
              <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> <span>{settings.company_mobile || '+971 50 399 2163'}</span>
            </a>
            <a href={`tel:${settings.company_phone || '+97142727815'}`} className="hidden lg:flex items-center gap-1 sm:gap-1.5 hover:text-[#0c1b44] transition-colors">
              <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> <span>{settings.company_phone || '+971 4 272 7815'}</span>
            </a>
            <a href={`mailto:${settings.company_email || 'sales@gpgt.ae'}`} className="hidden md:flex items-center gap-1.5 hover:text-[#0c1b44] transition-colors">
              <Mail className="h-3.5 w-3.5" /> {settings.company_email || 'sales@gpgt.ae'}
            </a>
          </div>
          <div className="hidden md:flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-[#0c1b44]" />
            <span>{settings.company_address || 'Shop No:3 Al Sajaya Building, Al Qusais 2, Damascus St.  244, Sector, Dubai, United Arab Emirates'}</span>
          </div>
        </div>
      </div>

      {/* Main Header - Compact on mobile */}
      <div className="bg-white py-1.5 md:py-4 shadow-sm">
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex items-center w-full">
            {/* Logo — smaller on mobile */}
            <Link to="/" className="flex items-center flex-shrink-0 md:mr-[10px]" data-testid="logo-link">
              {!logoError ? (
                <span className="inline-flex items-center justify-center bg-white rounded-full shadow-sm border border-gray-100 px-3 py-1.5 md:px-4 md:py-2">
                  <img
                    src={settings.header_logo ? (settings.header_logo.startsWith('/api') ? `${window.location.origin}${settings.header_logo}` : settings.header_logo) : `${process.env.PUBLIC_URL}/logo.png`}
                    alt="Grand Palace General Trading"
                    className="h-[48px] md:h-[66px] lg:h-[84px] w-auto object-contain object-left"
                    onError={() => setLogoError(true)}
                  />
                </span>
              ) : (
                <div className="flex flex-col leading-tight">
                  <span className="text-[#0c1b44] font-bold text-sm md:text-xl leading-tight">Grand Palace</span>
                  <span className="text-gray-500 font-medium text-[10px] md:text-sm tracking-wide">General Trading</span>
                </div>
              )}
            </Link>

            {/* Search Bar — Desktop only */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 w-full ml-4 mr-4 lg:ml-8 lg:mr-8 transition-all duration-300">
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="Search products, categories, brands..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white border-2 border-gray-200 rounded-lg pr-12 h-12 text-navy placeholder:text-gray-400 focus:border-[#0c1b44]"
                  data-testid="search-input"
                />
                <Button type="submit" className="absolute right-0 top-0 h-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white rounded-l-none rounded-r-lg px-4">
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </form>

            {/* Right Actions */}
            <div className="flex items-center gap-2 md:gap-4 ml-auto flex-shrink-0">
              {user ? (
                <div className="hidden md:flex items-center gap-3">
                  <Link to="/account">
                    <Button variant="ghost" className="text-navy hover:text-white hover:bg-[#0c1b44] transition-colors flex items-center gap-1.5" data-testid="account-btn">
                      <User className="h-5 w-5" />
                      <span className="text-sm font-medium">Account</span>
                    </Button>
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin">
                      <Button variant="ghost" className="text-navy hover:text-white hover:bg-[#0c1b44] transition-colors flex items-center gap-1.5" data-testid="admin-btn">
                        <LayoutDashboard className="h-5 w-5" />
                        <span className="text-sm font-medium">Admin</span>
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <Link to="/login" className="hidden md:block">
                  <Button variant="ghost" className="text-navy hover:text-white hover:bg-[#0c1b44] transition-colors flex items-center gap-1.5" data-testid="login-btn">
                    <User className="h-5 w-5" />
                    <span className="text-sm font-medium">Login</span>
                  </Button>
                </Link>
              )}

              {/* Wishlist — visible on desktop */}
              <Link to="/wishlist" className="relative hidden md:block" data-testid="wishlist-link">
                <Button variant="ghost" className="text-navy hover:text-white hover:bg-[#0c1b44] transition-colors flex items-center gap-1.5">
                  <Heart className="h-5 w-5 text-rose-500 md:text-inherit" />
                  <span className="text-sm font-medium">Wishlist</span>
                </Button>
              </Link>

              {/* Cart — visible on desktop only (mobile uses bottom nav) */}
              <Link to="/cart" className="relative hidden md:block" data-testid="cart-link">
                <Button variant="ghost" className="text-navy hover:text-white hover:bg-[#0c1b44] transition-colors flex items-center gap-1.5">
                  <ShoppingCart className="h-5 w-5" />
                  <span className="text-sm font-medium">Cart</span>
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#0c1b44] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Hamburger — mobile only */}
              <Button variant="ghost" className="md:hidden text-navy p-1" onClick={() => setMobileMenuOpen(true)} data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Desktop only */}
      <nav ref={navRef} className="hidden md:block bg-white relative border-y border-gray-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <ul className="flex items-center">
              <li>
                <Link to="/" className="text-sm font-bold text-gray-700 hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors block">
                  Home
                </Link>
              </li>
              {/* About Us Dropdown */}
              <li className="relative group">
                <Link to="/about" className="flex items-center gap-1.5 text-sm font-bold text-gray-700 group-hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors">
                  About Us
                  <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                </Link>
                
                <div className="absolute top-full left-0 w-56 bg-white border border-gray-100 shadow-xl rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <ul className="py-2">
                    <li>
                      <Link to="/about?tab=profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0c1b44]">Company Profile</Link>
                    </li>
                    <li>
                      <Link to="/about?tab=mission" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0c1b44]">Vision & Mission</Link>
                    </li>
                    <li>
                      <Link to="/about?tab=why-us" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0c1b44]">Why Choose Us</Link>
                    </li>
                    <li>
                      <Link to="/about?tab=milestones" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0c1b44]">Company Milestones</Link>
                    </li>
                    <li>
                      <Link to="/industries" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0c1b44]">Industries We Serve</Link>
                    </li>
                  </ul>
                </div>
              </li>
              <li>
                <Link to="/services" className="text-sm font-bold text-gray-700 hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors block">
                  Services
                </Link>
              </li>
              
              <li>
                <Link to="/products" className="text-sm font-bold text-gray-700 hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors block">
                  Products
                </Link>
              </li>
              <li>
                <Link to="/catalog" className="text-sm font-bold text-gray-700 hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors block">
                  Catalog
                </Link>
              </li>


              <li>
                <Link to="/faq" className="text-sm font-bold text-gray-700 hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors block">
                  FAQ's
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm font-bold text-gray-700 hover:text-[#0c1b44] px-2 xl:px-4 py-4 transition-colors block whitespace-nowrap">
                  Contact Us
                </Link>
              </li>
            </ul>
            <div className="flex items-center gap-3">
              <a href="#" title="GranLace" className="bg-white border border-gray-200 rounded-full px-3 py-1 shadow-md hover:shadow-lg transition-shadow flex items-center">
                <img src="/images/granlace.jpg" alt="GranLace" className="h-6 w-auto" />
              </a>
              <a href="https://grandpalace.ae" title="GranZ" target="_blank" rel="noreferrer" className="bg-white border border-gray-200 rounded-full px-3 py-1 shadow-md hover:shadow-lg transition-shadow flex items-center">
                <img src="/images/granz.jpg" alt="GranZ" className="h-6 w-auto" />
              </a>
              <a href="#" title="GranStor" className="bg-white border border-gray-200 rounded-full px-3 py-1 shadow-md hover:shadow-lg transition-shadow flex items-center">
                <img src="/images/granstor.jpg" alt="GranStor" className="h-6 w-auto" />
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="bg-white border-r border-gray-200 w-[300px] overflow-y-auto z-[100]">
          <SheetHeader>
            <SheetTitle className="text-[#0c1b44] text-left flex items-center justify-between">
              <span>Menu</span>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus-visible:ring-[#0c1b44]"
              />
            </form>
            <nav className="space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
              >
                Home
              </Link>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="about" className="border-none">
                  <AccordionTrigger className="hover:no-underline py-3 px-3 text-navy font-semibold hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span>About Us</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-1 pl-6 pr-4">
                    <div className="flex flex-col space-y-1">
                      <Link to="/about?tab=profile" onClick={() => setMobileMenuOpen(false)} className="py-2 text-sm text-gray-600 hover:text-[#0c1b44]">Company Profile</Link>
                      <Link to="/about?tab=mission" onClick={() => setMobileMenuOpen(false)} className="py-2 text-sm text-gray-600 hover:text-[#0c1b44]">Vision & Mission</Link>
                      <Link to="/about?tab=why-us" onClick={() => setMobileMenuOpen(false)} className="py-2 text-sm text-gray-600 hover:text-[#0c1b44]">Why Choose Us</Link>
                      <Link to="/about?tab=milestones" onClick={() => setMobileMenuOpen(false)} className="py-2 text-sm text-gray-600 hover:text-[#0c1b44]">Company Milestones</Link>
                      <Link to="/industries" onClick={() => setMobileMenuOpen(false)} className="py-2 text-sm text-gray-600 hover:text-[#0c1b44]">Industries We Serve</Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <Link
                to="/services"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
              >
                Services
              </Link>

              <Link
                to="/products"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-3"
              >
                <span>Products</span>
              </Link>
              <Link
                to="/catalog"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-3"
              >
                <span>Catalog</span>
              </Link>

              <Link
                to="/wishlist"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <Heart className="h-4 w-4 text-rose-500" /> My Wishlist
              </Link>

              <div className="pt-2">
                <p className="text-[#0c1b44] text-xs font-semibold uppercase tracking-wide px-3 pb-2">Categories</p>
                <Accordion type="single" collapsible className="w-full">
                  {categories.map(cat => {
                    const CatIcon = iconMap[cat.icon] || Package;
                    const subcategories = cat.children || [];
                    
                    if (subcategories.length === 0) {
                      return (
                        <Link
                          key={cat.id}
                          to={`/products?category=${cat.id}`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <CatIcon className="h-4 w-4 text-[#0c1b44]" />
                          {cat.name}
                        </Link>
                      );
                    }

                    return (
                      <AccordionItem key={cat.id} value={cat.id} className="border-none">
                        <AccordionTrigger className="hover:no-underline py-3 px-3 text-navy font-semibold hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CatIcon className="h-4 w-4 text-[#0c1b44]" />
                            <span>{cat.name}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-1 pl-10 pr-4">
                          <div className="flex flex-col space-y-1">
                            <Link 
                              to={`/products?category=${cat.id}`}
                              onClick={() => setMobileMenuOpen(false)}
                              className="py-2 text-sm text-gray-600 hover:text-[#0c1b44]"
                            >
                              All {cat.name}
                            </Link>
                            {subcategories.map(sub => (
                              <Link
                                key={sub.id}
                                to={`/products?category=${sub.id}`}
                                onClick={() => setMobileMenuOpen(false)}
                                className="py-2 text-sm text-gray-600 hover:text-[#0c1b44] flex items-center justify-between"
                              >
                                {sub.name}
                                {sub.product_count > 0 && (
                                  <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-400">
                                    {sub.product_count}
                                  </span>
                                )}
                              </Link>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>

              <Separator className="bg-navy-light my-4" />


              <Separator className="bg-navy-light my-4" />

              <Link
                to="/faq"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
              >
                FAQ's
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
              >
                Contact Us
              </Link>

              {user ? (
                <>
                  <Link
                    to="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    My Account
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-3 px-3 text-[#0c1b44] hover:text-[#0c1b44]-light hover:bg-gray-100 rounded-lg transition-colors font-semibold"
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="block w-full text-left py-3 px-3 text-red-400 hover:text-red-300 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-3 px-3 text-navy font-semibold hover:text-[#0c1b44] hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-3 px-3 text-[#0c1b44] hover:text-[#0c1b44]-light hover:bg-gray-100 rounded-lg transition-colors font-semibold"
                  >
                    Register
                  </Link>
                </>
              )}
            </nav>
            <div className="flex flex-col gap-3 mt-8">
              <a href="#" title="GranLace" className="bg-white border border-gray-200 rounded-full px-4 py-2 shadow-md flex items-center justify-center">
                <img src="/images/granlace.jpg" alt="GranLace" className="h-7 w-auto" />
              </a>
              <a href="https://grandpalace.ae" title="GranZ" target="_blank" rel="noreferrer" className="bg-white border border-gray-200 rounded-full px-4 py-2 shadow-md flex items-center justify-center">
                <img src="/images/granz.jpg" alt="GranZ" className="h-7 w-auto" />
              </a>
              <a href="#" title="GranStor" className="bg-white border border-gray-200 rounded-full px-4 py-2 shadow-md flex items-center justify-center">
                <img src="/images/granstor.jpg" alt="GranStor" className="h-7 w-auto" />
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
